const RESERVED_TENANT_HOSTS = new Set(['www', 'api', 'admin']);
export const SINGLE_SITE_TENANT_SLUG = 'slotsoceans';
const SINGLE_SITE_HOSTS = new Set(['slotsoceans.com', 'www.slotsoceans.com', 'login.soagent.org']);
const SINGLE_SITE_MODE = String(process.env.REACT_APP_SINGLE_SITE_MODE ?? 'true').toLowerCase() !== 'false';
const PLATFORM_DOMAIN = String(process.env.REACT_APP_PLATFORM_DOMAIN || 'playemp.vip')
  .trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
export const CONTROL_PLANE_DOMAIN = String(process.env.REACT_APP_CONTROL_PLANE_DOMAIN || '')
  .trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
const AGENTS_HUB_DOMAIN_RAW = String(process.env.REACT_APP_AGENTS_HUB_DOMAIN || '')
  .trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
const PLATFORM_ROOT_HOSTS = new Set([
  PLATFORM_DOMAIN,
  `www.${PLATFORM_DOMAIN}`,
  CONTROL_PLANE_DOMAIN,
  CONTROL_PLANE_DOMAIN ? `www.${CONTROL_PLANE_DOMAIN}` : '',
  // The Agents Hub is a platform surface, not a tenant — without this its
  // host resolves as a tenant slug and drags in the player theme boot.
  AGENTS_HUB_DOMAIN_RAW,
  AGENTS_HUB_DOMAIN_RAW ? `www.${AGENTS_HUB_DOMAIN_RAW}` : '',
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
]);
const RESERVED_ROOT_SEGMENTS = new Set([
  'auth', 'admin', 'app', 'api', 'icons', 'static', 'staff', 'preview',
  'staff-portal', 'agent-portal', 'finance', 'survey', 'deposit-return', 'portal', 'uploads',
  '__tenant', '__tenant-preview', 'download-app', 'agent', 'agents-hub',
  'legal', 'terms', 'privacy', 'v2', 'ui-preview',
]);

const normalize = (value) => String(value || '').split(':')[0].trim().toLowerCase().replace(/\.$/, '');
const validSlug = (value) => /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(value);

export function isAllowedTenantSegment(segment) {
  const value = normalize(segment);
  if (!value || RESERVED_ROOT_SEGMENTS.has(value)) return false;
  return SINGLE_SITE_MODE
    ? value === SINGLE_SITE_TENANT_SLUG || SINGLE_SITE_HOSTS.has(value)
    : validSlug(value);
}

// Backward-compatible export for existing imports while multi-tenant mode is
// activated through build configuration.
export const isAllowedSingleSiteTenantSegment = isAllowedTenantSegment;

export function tenantSlugFromHost(hostname = window.location.hostname) {
  const host = normalize(hostname);
  if (!host) return null;
  if (SINGLE_SITE_HOSTS.has(host)) return SINGLE_SITE_TENANT_SLUG;
  if (PLATFORM_ROOT_HOSTS.has(host)) return null;

  const parts = host.split('.').filter(Boolean);
  const platformParts = PLATFORM_DOMAIN.split('.').filter(Boolean);
  const suffixMatches = platformParts.length > 0
    && parts.length > platformParts.length
    && parts.slice(-platformParts.length).join('.') === PLATFORM_DOMAIN;
  if (suffixMatches) {
    const prefix = parts.slice(0, -platformParts.length);
    const subdomain = prefix[0] === 'www' ? prefix[1] : prefix[0];
    if (!subdomain || RESERVED_TENANT_HOSTS.has(subdomain)) return null;
    return SINGLE_SITE_MODE
      ? (subdomain === SINGLE_SITE_TENANT_SLUG ? SINGLE_SITE_TENANT_SLUG : null)
      : subdomain;
  }

  // A custom player or agent domain is itself a valid tenant lookup key. The
  // backend resolves it to the canonical tenant and its domain role.
  if (!SINGLE_SITE_MODE && host.includes('.')) return host;
  return null;
}

export function tenantSlugFromPathOrHost(
  pathname = window.location.pathname,
  hostname = window.location.hostname,
) {
  const parts = String(pathname || '').split('/').filter(Boolean);
  if (parts.length > 0) {
    const first = normalize(parts[0]);
    if (isAllowedTenantSegment(first)) {
      return SINGLE_SITE_MODE ? SINGLE_SITE_TENANT_SLUG : first;
    }
  }
  return tenantSlugFromHost(hostname);
}

export function isReservedTenantSegment(segment) {
  const value = normalize(segment);
  return !value || RESERVED_ROOT_SEGMENTS.has(value);
}

export function isSingleSiteMode() {
  return SINGLE_SITE_MODE;
}

export function isControlPlaneHost(hostname = window.location.hostname) {
  const host = normalize(hostname);
  return Boolean(CONTROL_PLANE_DOMAIN && [CONTROL_PLANE_DOMAIN, `www.${CONTROL_PLANE_DOMAIN}`].includes(host));
}

// The Agents Hub oversight portal lives on its own subdomain.
export const AGENTS_HUB_DOMAIN = AGENTS_HUB_DOMAIN_RAW;

export function isAgentsHubHost(hostname = window.location.hostname) {
  const host = normalize(hostname);
  return Boolean(AGENTS_HUB_DOMAIN && [AGENTS_HUB_DOMAIN, `www.${AGENTS_HUB_DOMAIN}`].includes(host));
}
